"use client";

import { Trash2 } from "lucide-react";
import { useTranslations } from "next-intl";
import { useState } from "react";

import { ColorPicker } from "@/components/shared/ColorPicker";
import { ConfirmDialog } from "@/components/shared/ConfirmDialog";
import { Dialog } from "@/components/shared/Dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";

interface ViewSharedListModalProps {
  isOpen: boolean;
  listName: string;
  listColor: string;
  colorOptions: string[];
  onClose: () => void;
  onLeave: () => Promise<void>;
}

export function ViewSharedListModal({
  isOpen,
  listName,
  listColor,
  colorOptions,
  onClose,
  onLeave,
}: ViewSharedListModalProps) {
  const t = useTranslations("lists");
  const [showLeaveConfirmDialog, setShowLeaveConfirmDialog] = useState(false);
  const [isLeaving, setIsLeaving] = useState(false);

  const handleLeave = async () => {
    setIsLeaving(true);
    try {
      await onLeave();
      setShowLeaveConfirmDialog(false);
    } finally {
      setIsLeaving(false);
    }
  };

  return (
    <Dialog
      isOpen={isOpen}
      onClose={() => !isLeaving && onClose()}
      disableOutsideClick={isLeaving}
      title={t("viewSharedList")}
      description={t("sharedWithYou")}
    >
      <div className="space-y-6">
        <div className="flex flex-col space-y-4">
          <Label htmlFor="shared-list-name">{t("listName")}</Label>
          <Input
            id="shared-list-name"
            value={listName}
            disabled
            className="opacity-60"
            maxLength={100}
          />
        </div>

        <div className="flex flex-col space-y-4">
          <Label>{t("listColor")}</Label>
          <ColorPicker colors={colorOptions} selected={listColor} disabled />
        </div>
      </div>

      {/* Leave option for shared lists */}
      <div className="border-t border-border pt-4 sm:pt-6">
        <Button
          variant="destructive"
          className="w-full"
          onClick={() => setShowLeaveConfirmDialog(true)}
          disabled={isLeaving}
        >
          <Trash2 className="size-4" />
          {t("leaveList")}
        </Button>
      </div>

      {/* Leave confirmation dialog */}
      <ConfirmDialog
        isOpen={showLeaveConfirmDialog}
        onClose={() => setShowLeaveConfirmDialog(false)}
        onConfirm={handleLeave}
        title={t("leaveSharedListTitle")}
        description={t("leaveSharedListMessage")}
        confirmText={t("leave")}
        confirmIcon={<Trash2 className="size-4" />}
        cancelText={t("cancel")}
        variant="destructive"
        loading={isLeaving}
      />
    </Dialog>
  );
}
